import { useState, useEffect, useCallback } from 'react';
import {
  getIvrQuestions,
  updateIvrQuestion,
  getIvrQuestionOverrides,
  createIvrQuestionOverride,
  updateIvrQuestionOverride,
  deleteIvrQuestionOverride,
} from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import './IvrQuestionsTab.css';

// ── Default form state helpers ────────────────────────────────────────────────

const DEFAULT_EDIT = { textEN: '', textES: '', rejectionEN: '' };
const DEFAULT_OVERRIDE = { companyId: '', textEN: '', textES: '', rejectionEN: '' };

// ── QuestionRow ───────────────────────────────────────────────────────────────

function QuestionRow({ question, onSaved }) {
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(DEFAULT_EDIT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Overrides panel
  const [showOverrides, setShowOverrides] = useState(false);
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overrideError, setOverrideError] = useState('');

  // Override form
  const [overrideForm, setOverrideForm] = useState(null); // null=hidden, {} = new, obj = editing
  const [overrideFormData, setOverrideFormData] = useState(DEFAULT_OVERRIDE);
  const [overrideSaving, setOverrideSaving] = useState(false);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const openEdit = () => {
    setForm({
      textEN: question.textEN ?? '',
      textES: question.textES ?? '',
      rejectionEN: question.rejectionEN ?? '',
    });
    setEditMode(true);
    setError('');
  };

  const cancelEdit = () => {
    setEditMode(false);
    setError('');
  };

  const saveEdit = async () => {
    if (!form.textEN.trim()) { setError('English text is required.'); return; }
    try {
      setSaving(true);
      setError('');
      await updateIvrQuestion(question.id, {
        textEN: form.textEN.trim(),
        textES: form.textES.trim() || null,
        rejectionEN: form.rejectionEN.trim() || null,
      });
      setSuccess('Saved.');
      setEditMode(false);
      onSaved();
    } catch (err) {
      setError('Failed to save: ' + parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const loadOverrides = useCallback(async () => {
    try {
      setOverridesLoading(true);
      setOverrideError('');
      setOverrides(await getIvrQuestionOverrides(question.id));
    } catch (err) {
      setOverrideError('Failed to load overrides: ' + parseApiError(err));
    } finally {
      setOverridesLoading(false);
    }
  }, [question.id]);

  const toggleOverrides = () => {
    if (!showOverrides) loadOverrides();
    setShowOverrides((v) => !v);
    setOverrideForm(null);
  };

  const openNewOverride = () => {
    setOverrideFormData(DEFAULT_OVERRIDE);
    setOverrideForm('new');
    setOverrideError('');
  };

  const openEditOverride = (ov) => {
    setOverrideFormData({
      companyId: ov.companyId,
      textEN: ov.textEN ?? '',
      textES: ov.textES ?? '',
      rejectionEN: ov.rejectionEN ?? '',
    });
    setOverrideForm(ov);
    setOverrideError('');
  };

  const cancelOverrideForm = () => {
    setOverrideForm(null);
    setOverrideError('');
  };

  const saveOverride = async () => {
    if (!overrideFormData.textEN.trim()) { setOverrideError('English text is required.'); return; }
    try {
      setOverrideSaving(true);
      setOverrideError('');
      const payload = {
        companyId: parseInt(overrideFormData.companyId),
        textEN: overrideFormData.textEN.trim(),
        textES: overrideFormData.textES.trim() || null,
        rejectionEN: overrideFormData.rejectionEN.trim() || null,
      };
      if (overrideForm === 'new') {
        if (!payload.companyId) { setOverrideError('Company ID is required.'); setOverrideSaving(false); return; }
        await createIvrQuestionOverride(question.id, payload);
      } else {
        await updateIvrQuestionOverride(question.id, overrideForm.id, {
          textEN: payload.textEN,
          textES: payload.textES,
          rejectionEN: payload.rejectionEN,
        });
      }
      setOverrideForm(null);
      await loadOverrides();
      onSaved();
    } catch (err) {
      setOverrideError('Failed to save override: ' + parseApiError(err));
    } finally {
      setOverrideSaving(false);
    }
  };

  const deactivateOverride = async (ov) => {
    if (!window.confirm(`Deactivate override for company "${ov.companyName ?? ov.companyId}"?`)) return;
    try {
      setOverrideError('');
      await deleteIvrQuestionOverride(question.id, ov.id);
      await loadOverrides();
      onSaved();
    } catch (err) {
      setOverrideError('Failed to deactivate: ' + parseApiError(err));
    }
  };

  const oField = (key, val) => setOverrideFormData((p) => ({ ...p, [key]: val }));

  return (
    <div className="iq-question-card">
      {/* Question header */}
      <div className="iq-question-header">
        <div className="iq-question-meta">
          <span className="iq-identifier">{question.identifier}</span>
          {question.formLocation && (
            <span className="iq-location">{question.formLocation}</span>
          )}
          <span className="iq-badge gather">{question.gatherDigits} digit{question.gatherDigits !== 1 ? 's' : ''}</span>
          {question.overrideCount > 0 && (
            <span className="iq-badge override-count">{question.overrideCount} override{question.overrideCount !== 1 ? 's' : ''}</span>
          )}
        </div>
        <button className="btn-edit-small" onClick={openEdit} disabled={editMode}>Edit</button>
      </div>

      {/* Current text (read mode) */}
      {!editMode && (
        <div className="iq-text-display">
          <div className="iq-lang-row">
            <span className="iq-lang-label">EN</span>
            <span className="iq-lang-text">{question.textEN || <em className="iq-empty">Not set — hardcoded fallback will be used</em>}</span>
          </div>
          {question.textES && (
            <div className="iq-lang-row">
              <span className="iq-lang-label">ES</span>
              <span className="iq-lang-text">{question.textES}</span>
            </div>
          )}
          {question.rejectionEN && (
            <div className="iq-lang-row rejection">
              <span className="iq-lang-label">Rejection</span>
              <span className="iq-lang-text">{question.rejectionEN}</span>
            </div>
          )}
        </div>
      )}

      {success && <div className="iq-success">{success}</div>}
      {error && <div className="iq-error">{error}</div>}

      {/* Edit form */}
      {editMode && (
        <div className="iq-edit-form">
          <div className="iq-field">
            <label>English Prompt</label>
            <textarea
              value={form.textEN}
              onChange={(e) => setForm((p) => ({ ...p, textEN: e.target.value }))}
              rows={3}
              placeholder="English IVR prompt text"
            />
          </div>
          <div className="iq-field">
            <label>Spanish Prompt <span className="field-hint">(optional)</span></label>
            <textarea
              value={form.textES}
              onChange={(e) => setForm((p) => ({ ...p, textES: e.target.value }))}
              rows={2}
              placeholder="Spanish IVR prompt text (optional)"
            />
          </div>
          <div className="iq-field">
            <label>Rejection Response <span className="field-hint">(optional — spoken on invalid input)</span></label>
            <textarea
              value={form.rejectionEN}
              onChange={(e) => setForm((p) => ({ ...p, rejectionEN: e.target.value }))}
              rows={2}
              placeholder="Text spoken when patient enters invalid input (optional)"
            />
          </div>
          <div className="iq-form-actions">
            <button className="btn-save" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="btn-cancel" onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      )}

      {/* Overrides section */}
      <div className="iq-overrides-toggle">
        <button className="btn-toggle-overrides" onClick={toggleOverrides}>
          {showOverrides ? '▲ Hide' : '▼ Show'} Company Overrides
          {question.overrideCount > 0 && ` (${question.overrideCount})`}
        </button>
      </div>

      {showOverrides && (
        <div className="iq-overrides-panel">
          {overrideError && <div className="iq-error">{overrideError}</div>}
          {overridesLoading && <p className="iq-loading">Loading overrides…</p>}

          {!overridesLoading && overrides.length === 0 && !overrideForm && (
            <p className="iq-empty-overrides">No company overrides. Click below to add one.</p>
          )}

          {!overridesLoading && overrides.map((ov) => (
            <div key={ov.id} className={`iq-override-row ${!ov.active ? 'inactive' : ''}`}>
              {overrideForm && overrideForm !== 'new' && overrideForm.id === ov.id ? (
                // Inline edit form for this override
                <div className="iq-override-form">
                  <div className="iq-field">
                    <label>English</label>
                    <textarea value={overrideFormData.textEN} onChange={(e) => oField('textEN', e.target.value)} rows={2} />
                  </div>
                  <div className="iq-field">
                    <label>Spanish <span className="field-hint">(optional)</span></label>
                    <textarea value={overrideFormData.textES} onChange={(e) => oField('textES', e.target.value)} rows={2} />
                  </div>
                  <div className="iq-field">
                    <label>Rejection <span className="field-hint">(optional)</span></label>
                    <textarea value={overrideFormData.rejectionEN} onChange={(e) => oField('rejectionEN', e.target.value)} rows={2} />
                  </div>
                  <div className="iq-form-actions">
                    <button className="btn-save" onClick={saveOverride} disabled={overrideSaving}>
                      {overrideSaving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="btn-cancel" onClick={cancelOverrideForm}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="iq-override-info">
                    <span className="iq-override-company">
                      {ov.companyName ?? `Company ${ov.companyId}`}
                    </span>
                    {!ov.active && <span className="iq-badge inactive">Inactive</span>}
                  </div>
                  <div className="iq-lang-row">
                    <span className="iq-lang-label">EN</span>
                    <span className="iq-lang-text">{ov.textEN}</span>
                  </div>
                  {ov.textES && (
                    <div className="iq-lang-row">
                      <span className="iq-lang-label">ES</span>
                      <span className="iq-lang-text">{ov.textES}</span>
                    </div>
                  )}
                  {ov.rejectionEN && (
                    <div className="iq-lang-row rejection">
                      <span className="iq-lang-label">Rejection</span>
                      <span className="iq-lang-text">{ov.rejectionEN}</span>
                    </div>
                  )}
                  <div className="iq-override-actions">
                    <button className="btn-edit-small" onClick={() => openEditOverride(ov)}>Edit</button>
                    {ov.active && (
                      <button className="btn-deactivate-small" onClick={() => deactivateOverride(ov)}>Deactivate</button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}

          {/* New override form */}
          {overrideForm === 'new' && (
            <div className="iq-override-form">
              <div className="iq-field">
                <label>Company ID</label>
                <input
                  type="number"
                  value={overrideFormData.companyId}
                  onChange={(e) => oField('companyId', e.target.value)}
                  placeholder="Company ID number"
                />
              </div>
              <div className="iq-field">
                <label>English</label>
                <textarea value={overrideFormData.textEN} onChange={(e) => oField('textEN', e.target.value)} rows={2} />
              </div>
              <div className="iq-field">
                <label>Spanish <span className="field-hint">(optional)</span></label>
                <textarea value={overrideFormData.textES} onChange={(e) => oField('textES', e.target.value)} rows={2} />
              </div>
              <div className="iq-field">
                <label>Rejection <span className="field-hint">(optional)</span></label>
                <textarea value={overrideFormData.rejectionEN} onChange={(e) => oField('rejectionEN', e.target.value)} rows={2} />
              </div>
              <div className="iq-form-actions">
                <button className="btn-save" onClick={saveOverride} disabled={overrideSaving}>
                  {overrideSaving ? 'Saving…' : 'Create'}
                </button>
                <button className="btn-cancel" onClick={cancelOverrideForm}>Cancel</button>
              </div>
            </div>
          )}

          {overrideForm === null && (
            <button className="btn-add-override" onClick={openNewOverride}>+ Add Company Override</button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main tab ──────────────────────────────────────────────────────────────────

export default function IvrQuestionsTab() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchQuestions = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setQuestions(await getIvrQuestions());
    } catch (err) {
      setError('Failed to load IVR questions: ' + parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  if (loading) return (
    <div className="ivr-questions-tab">
      <h2>IVR Question Wording</h2>
      <p className="iq-loading">Loading questions…</p>
    </div>
  );

  return (
    <div className="ivr-questions-tab">
      <div className="iq-tab-header">
        <div>
          <h2>IVR Question Wording</h2>
          <p className="iq-tab-desc">
            Edit the prompt text spoken during IVR calls. Changes take effect within 5 minutes (cache TTL).
            Company overrides take precedence over global defaults.
          </p>
        </div>
      </div>

      {error && <div className="iq-error">{error}</div>}

      {questions.length === 0 && !error && (
        <p className="iq-empty">No IVR questions found. Ensure questions are seeded with <code>bUsedInIVR = 1</code>.</p>
      )}

      <div className="iq-questions-list">
        {questions.map((q) => (
          <QuestionRow key={q.id} question={q} onSaved={fetchQuestions} />
        ))}
      </div>
    </div>
  );
}
